'use client';

import { BadgeSelect } from '@/app/(main)/clients/create/components/BadgeSelect';
import { IconButton } from '@/app/components/buttons/IconButton';
import {
  ACCOMMODATION_PREFERENCE_OPTIONS,
  type AccommodationPreference,
} from '@/types/clientsApiTypes';
import {
  mergeAccommodationTypes,
  partitionAccommodationTypes,
} from '@/utils/preferences/partitionAccommodationTypes';
import CloseTinyIcon from '/public/icons/close-tiny.svg';

export type StayAccommodationTypesFieldProps = {
  /** Raw API/form values: strings and/or `{ value, translated_value }` objects. */
  value: readonly unknown[] | null | undefined;
  onChange: (next: string[]) => void;
};

/**
 * Standard BadgeSelect plus legacy/free-text `stay.accommodation_types` rows (removable).
 */
export function StayAccommodationTypesField({ value, onChange }: StayAccommodationTypesFieldProps) {
  const rawList = value ?? [];
  const { legacy, known } = partitionAccommodationTypes(rawList);

  const removeLegacyAt = (legacyIndex: number) => {
    const nextLegacy = legacy.filter((_, i) => i !== legacyIndex);
    onChange(mergeAccommodationTypes(nextLegacy, known));
  };

  return (
    <div className="flex flex-col gap-3">
      <BadgeSelect<AccommodationPreference>
        label=""
        type="secondary"
        options={ACCOMMODATION_PREFERENCE_OPTIONS}
        value={known}
        onChange={(nextKnown) => onChange(mergeAccommodationTypes(legacy, nextKnown))}
      />
      {legacy.length > 0 && (
        <div className="flex flex-col gap-2 border-t border-grey-200 pt-3">
          <p className="text-body-small text-grey-700">
            Legacy preferences (not in the standard categories above). Remove any you no longer need; remaining entries
            stay on file when you save changes.
          </p>
          <div className="flex flex-wrap gap-2">
            {legacy.map((text, idx) => (
              <div
                key={`legacy-accommodation-${idx}-${text.slice(0, 48)}`}
                className="inline-flex max-w-full items-start gap-1 rounded-[12px] bg-[#F2F6FC] py-1 pl-2 pr-0.5 font-inter text-body-regular text-grey-700"
              >
                <span className="min-w-0 flex-1 whitespace-pre-wrap break-words pt-0.5">{text}</span>
                <IconButton
                  type="button"
                  size="small"
                  color="grey_700"
                  className="shrink-0"
                  aria-label={`Remove legacy accommodation preference: ${text.slice(0, 120)}`}
                  icon={<CloseTinyIcon className="h-3 w-3 text-grey-700" />}
                  onClick={() => removeLegacyAt(idx)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
